import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Table } from 'antd';

// 列宽下限：四个汉字字符的宽度（约 4em，按 12px 计约 48px）
const MIN_COLUMN_WIDTH = 48;
// 列宽上限：单列最大宽度，所有列表页统一，避免表格无限拉宽超出页面
const MAX_COLUMN_WIDTH = 600;
// 有 rowSelection 时选择列占用的宽度，必须计入 scroll.x 否则数据列会被撑满无法拖窄
const SELECTION_COLUMN_WIDTH = 52;

/**
 * 可调整列宽的表格组件
 * @param {Array} columns - 列配置
 * @param {Array} dataSource - 数据源
 * @param {String} storageKey - localStorage存储key（用于保存列宽）
 * @param {Object} tableProps - 其他Table组件的props
 */
const ResizableTable = ({ columns: originalColumns, dataSource, storageKey, ...tableProps }) => {
  const [columns, setColumns] = useState(originalColumns);
  const [selectedRowKey, setSelectedRowKey] = useState(null);

  // 从localStorage加载保存的列宽（仅首次或 storageKey 变化）
  useEffect(() => {
    if (storageKey) {
      const saved = localStorage.getItem(`${storageKey}_widths`);
      if (saved) {
        try {
          const savedWidths = JSON.parse(saved);
          setColumns(prevColumns =>
            prevColumns.map(col => {
              if (savedWidths[col.key] && col.key) {
                return { ...col, width: savedWidths[col.key] };
              }
              return col;
            })
          );
        } catch (e) {
          console.error('Failed to parse saved column widths:', e);
        }
      }
    }
  }, [storageKey]);

  // 须随 originalColumns 引用同步（含 render/onClick 新闭包），否则列 key 不变时操作列会一直用首次挂载的旧列
  useEffect(() => {
    const cols = originalColumns || [];
    if (storageKey) {
      const saved = localStorage.getItem(`${storageKey}_widths`);
      if (saved) {
        try {
          const savedWidths = JSON.parse(saved);
          setColumns(
            cols.map((col) => {
              if (savedWidths[col.key] && col.key) {
                return { ...col, width: savedWidths[col.key] };
              }
              return col;
            })
          );
        } catch (e) {
          setColumns(cols);
        }
      } else {
        setColumns(cols);
      }
    } else {
      setColumns(cols);
    }
  }, [storageKey, originalColumns]);

  // 处理列宽调整
  const handleResizeStart = (index, startX, startWidth) => {
    let currentColumns = [...columns];
    
    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, Math.min(MAX_COLUMN_WIDTH, startWidth + diff));
      
      currentColumns[index] = {
        ...currentColumns[index],
        width: newWidth,
      };
      
      setColumns([...currentColumns]);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      
      // 保存到localStorage
      if (storageKey) {
        const widths = {};
        currentColumns.forEach(col => {
          if (col.key && col.width) {
            widths[col.key] = col.width;
          }
        });
        localStorage.setItem(`${storageKey}_widths`, JSON.stringify(widths));
        
        // 触发自定义事件，通知列设置页面更新
        window.dispatchEvent(new CustomEvent('columnWidthChanged', {
          detail: { storageKey: `${storageKey}_widths`, widths }
        }));
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // 可调整大小的表头组件
  const ResizableTitle = (props) => {
    const { width, index, ...restProps } = props;
    const thRef = useRef(null);

    if (!width) {
      return <th {...restProps} ref={thRef} />;
    }

    return (
      <th 
        {...restProps} 
        ref={thRef}
        style={{ 
          position: 'relative',
          minWidth: 0, // 移除最小宽度限制
          width: width, // 明确设置宽度，确保可以自由调整
          ...restProps.style 
        }}
      >
        <div style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          width: '100%',
          maxWidth: '100%'
        }}>
          {restProps.children}
        </div>
        <span
          className="react-resizable-handle"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (thRef.current) {
              const rect = thRef.current.getBoundingClientRect();
              handleResizeStart(index, e.clientX, rect.width);
            }
          }}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '10px',
            cursor: 'col-resize',
            zIndex: 1,
          }}
        />
      </th>
    );
  };

  // 处理列配置，添加onHeaderCell
  const processedColumns = useMemo(() => {
    return columns.map((col, index) => {
      if (!col.width || col.fixed) {
        return col;
      }
      return {
        ...col,
        width: col.width, // 确保宽度被正确设置
        onHeaderCell: (column) => ({
          width: column.width,
          index: index,
        }),
      };
    });
  }, [columns]);

  // 根据当前列宽之和动态计算 scroll.x，避免固定值导致列被强制撑满、无法拖窄
  const scrollX = useMemo(() => {
    const total = columns.reduce((sum, col) => sum + (Number(col.width) || 0), 0);
    const minTotal = columns.length * MIN_COLUMN_WIDTH;
    const dataWidth = Math.max(minTotal, Math.ceil(total));
    const selectionWidth = tableProps.rowSelection ? SELECTION_COLUMN_WIDTH : 0;
    return dataWidth + selectionWidth;
  }, [columns, tableProps.rowSelection]);

  const mergedScroll = useMemo(() => ({
    ...tableProps.scroll,
    x: scrollX,
  }), [tableProps.scroll, scrollX]);

  // 行点击选中高亮（用于除首页、系统设置外的列表页）
  const rowKeyProp = tableProps.rowKey;
  const getRowKey = (record) =>
    rowKeyProp == null ? undefined : (typeof rowKeyProp === 'function' ? rowKeyProp(record) : record?.[rowKeyProp]);
  const mergedOnRow = (record) => {
    const existing = tableProps.onRow?.(record) || {};
    if (rowKeyProp == null) return existing;
    const key = getRowKey(record);
    return {
      ...existing,
      onClick: (e) => {
        setSelectedRowKey((prev) => (prev === key ? prev : key));
        existing.onClick?.(e);
      },
    };
  };
  const mergedRowClassName = (record) => {
    const base = typeof tableProps.rowClassName === 'function' ? tableProps.rowClassName(record) : tableProps.rowClassName || '';
    if (rowKeyProp == null) return base || undefined;
    const key = getRowKey(record);
    const selected = key != null && key === selectedRowKey ? 'resizable-table-row-selected' : '';
    return [base, selected].filter(Boolean).join(' ') || undefined;
  };

  return (
    <div
      className="resizable-table-wrapper"
      style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}
    >
      <Table
        {...tableProps}
        className={[tableProps.className, 'resizable-table'].filter(Boolean).join(' ')}
        columns={processedColumns}
        dataSource={dataSource}
        scroll={mergedScroll}
        onRow={mergedOnRow}
        rowClassName={mergedRowClassName}
        components={{
          header: {
            cell: (props) => {
              const { width, index, ...restProps } = props;
              if (!width || props.fixed) {
                return <th {...restProps} />;
              }
              return <ResizableTitle {...props} index={index} />;
            },
          },
        }}
      />
    </div>
  );
};

export default ResizableTable;

